import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LogoMark from "../assets/22N.png";
import { IOS_APP_STORE_URL } from "../utils/appStore";
import { assignBannerVariant, BANNER_VARIANTS } from "../utils/appBannerVariant";
import { trackAppBannerViewed } from "../utils/posthogEvents";
import AppStoreLink from "./AppStoreLink";
import "../styles/app-banner.css";

/**
 * Site-wide "download the app" bar.
 *
 * Three visual variants are tested against each other (purple / glass / dock,
 * see utils/appBannerVariant.js). Every visitor keeps one variant, and both the
 * impression (`app_banner_viewed`) and the store click (`download_app_clicked`
 * with `banner_variant`) carry the variant, so click-through per variant is a
 * plain ratio in PostHog.
 *
 * Motion: the bar drops in from behind the top edge like a curtain, rolls up
 * when the visitor scrolls down, and drops back when they scroll up. The bar
 * reserves its own height through `--app-banner-height`, so every fixed header
 * (TopBar, HomeTopBanner, SharedFilterSection) rides up and down with it
 * instead of jumping. Body padding is set once and never animated, which is
 * what stops the feed from shifting under the user's thumb mid-scroll.
 */

const EASE_IN_OUT_CUBIC = (k) =>
  k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function ArrowIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h13M12 5l7 7-7 7" />
    </svg>
  );
}

/**
 * The bar's visual markup, shared by the live bar and the analytics preview so
 * the two can never drift. `interactive={false}` renders inert spans with the
 * same classes: no store link, and therefore no `download_app_clicked` event.
 */
export function BannerSurface({ variant, title, subtitle, cta, interactive = true }) {
  const Left = interactive ? AppStoreLink : "span";
  const Right = interactive ? AppStoreLink : "span";

  const leftProps = interactive
    ? {
        location: "app_download_banner",
        variant,
        href: IOS_APP_STORE_URL,
        className: "ab-left",
      }
    : { className: "ab-left" };

  const rightProps = interactive
    ? {
        location: "app_download_banner",
        variant,
        href: IOS_APP_STORE_URL,
        className: "ab-cta",
      }
    : { className: "ab-cta" };

  return (
    <>
      <div className="ab-surface">
        {variant === "purple" && (
          <>
            <span className="ab-glow" aria-hidden="true" />
            <span className="ab-sheen" aria-hidden="true" />
          </>
        )}
        {variant === "dock" && <span className="ab-neon" aria-hidden="true" />}

        <div className="ab-inner">
          <Left {...leftProps}>
            <span className="ab-mark">
              <img src={LogoMark} alt="22night" />
            </span>
            <span className="ab-text">
              <span className="ab-title">{title}</span>
              <span className="ab-sub">{subtitle}</span>
            </span>
          </Left>

          <Right {...rightProps}>
            {cta}
            <ArrowIcon />
          </Right>
        </div>
      </div>

      {variant === "glass" && <span className="ab-rail" aria-hidden="true" />}
    </>
  );
}

export default function AppDownloadBanner() {
  const { t } = useTranslation();
  const [variant] = useState(() => {
    const picked = assignBannerVariant();
    return BANNER_VARIANTS.includes(picked) ? picked : "purple";
  });

  const barRef = useRef(null);
  const heightRef = useRef(0);
  const progressRef = useRef(0);
  const rafRef = useRef(0);
  const busyRef = useRef(false);
  // Only scroll-driven moves drag the fixed headers with them. The entrance
  // keeps headers still, so nothing overlaps the page while it plays.
  const followHeaderRef = useRef(false);

  const paint = useCallback((p, follow = followHeaderRef.current) => {
    const el = barRef.current;
    if (!el || !Number.isFinite(p)) return;
    progressRef.current = p;
    el.style.transform = `translate3d(0, ${(p - 1) * 100}%, 0)`;
    el.style.clipPath = `inset(0 0 ${Math.max(0, 1 - p) * 100}% 0)`;
    const hidden = p < 0.5;
    el.setAttribute("aria-hidden", hidden ? "true" : "false");
    if (hidden) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
    const h = heightRef.current;
    if (h > 0) {
      const value = follow ? h * Math.min(1, Math.max(0, p)) : h;
      document.documentElement.style.setProperty("--app-banner-height", `${value}px`);
    }
  }, []);

  const animate = useCallback(
    (to, duration) => {
      const from = progressRef.current;
      if (Math.abs(to - from) < 0.001) return;
      cancelAnimationFrame(rafRef.current);
      const dur = duration || (to > from ? 820 : 520);
      const started = performance.now();
      busyRef.current = true;
      const step = (now) => {
        const k = Math.min(1, (now - started) / dur);
        let p = from + (to - from) * EASE_IN_OUT_CUBIC(k);
        // A 3% overshoot on the way in reads as the curtain settling, not snapping.
        if (to > from && k > 0.82) p += Math.sin(((k - 0.82) / 0.18) * Math.PI) * 0.035;
        paint(k < 1 ? p : to);
        if (k < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          busyRef.current = false;
        }
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [paint]
  );

  // Height bookkeeping + entrance.
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;

    const measure = () => {
      heightRef.current = el.offsetHeight;
      // Reserved space is constant: the curtain moves, the page does not.
      document.body.style.paddingTop = `${heightRef.current}px`;
      paint(progressRef.current);
    };

    paint(0);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    let timer = 0;
    if (prefersReducedMotion()) {
      paint(1, false);
    } else {
      timer = window.setTimeout(() => {
        followHeaderRef.current = false;
        animate(1, 820);
      }, 80);
    }

    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      document.body.style.paddingTop = "";
      document.documentElement.style.removeProperty("--app-banner-height");
    };
  }, [animate, paint]);

  // Curtain follows the scroll direction.
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (busyRef.current) return;

        if (y < 48) {
          if (progressRef.current < 0.999) {
            followHeaderRef.current = true;
            animate(1, 520);
          }
          return;
        }
        if (y < 160) return;

        if (delta > 10 && progressRef.current > 0.001) {
          followHeaderRef.current = true;
          animate(0, 460);
        } else if (delta < -10 && progressRef.current < 0.999) {
          followHeaderRef.current = true;
          animate(1, 620);
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [animate]);

  // One impression per session per variant.
  useEffect(() => {
    trackAppBannerViewed({ variant });
  }, [variant]);

  return (
    <div
      ref={barRef}
      className={`ab-bar ab-bar--${variant}`}
      data-banner-variant={variant}
      role="complementary"
      aria-label={t("app_banner_title")}
    >
      <BannerSurface
        variant={variant}
        title={t("app_banner_title")}
        subtitle={t("app_banner_subtitle")}
        cta={t("app_banner_cta")}
      />
    </div>
  );
}
